import Experience from "@folio-2026/experience"
import { EVENTS } from "@folio-2026/shared"
import { Text, TextScene } from "@folio-2026/text"
import gsap from "gsap"
import { gaussianBlur } from "three/examples/jsm/tsl/display/GaussianBlurNode.js"
import { clamp, float, mix, texture, uniform, uv, vec2, vec4 } from "three/tsl"
import * as THREE from "three/webgpu"
import type { FolderApi } from "tweakpane"

export default class Menu {
  fbo: THREE.RenderTarget
  experience: Experience
  renderer: Experience["renderer"]["instance"]
  globalResources: Experience["globalResources"]
  sizes: Experience["sizes"]
  perlin: THREE.Texture
  textScene: TextScene
  /** Text instances ordered top-to-bottom (cached at init, not re-queried). */
  private textStack: Text[] = []
  /** Pending staggered reveal delayed calls; killed on close so no reveal runs after. */
  private revealDelayedCalls: gsap.core.Tween[] = []
  /** Tween animating perlin multiplier to "open" state; killed on close so it does not override close. */
  private perlinOpenTween?: gsap.core.Tween
  debugFolder?: FolderApi

  settings = {
    blurStrength: uniform(3),
    perlinPower: uniform(2),
    perlinMultiplier: uniform(0),
    perlinAmplitude: uniform(1.5),
    perlinUvScale: uniform(0.5),
    darkenPercentage: uniform(80),
  }

  constructor({ debugFolder }: { debugFolder?: FolderApi } = {}) {
    this.experience = Experience.getInstance()
    this.debugFolder = debugFolder
    this.renderer = this.experience.renderer.instance
    this.globalResources = this.experience.globalResources
    this.sizes = this.experience.sizes

    this.fbo = new THREE.RenderTarget(this.sizes.width, this.sizes.height)

    this.perlin = this.globalResources.perlin as THREE.Texture
    this.perlin.wrapS = THREE.RepeatWrapping
    this.perlin.wrapT = THREE.RepeatWrapping
    this.perlin.minFilter = THREE.LinearFilter

    this.textScene = new TextScene("menu", {
      perlinTexture: this.perlin,
      debugFolder: this.debugFolder,
    })

    this.buildTextStack()
    this.textScene.texts.forEach((t) => t.setupEventListeners())
    this.setDebug()
    this.setEventListeners()
  }

  /**
   * Builds a cached list of text elements ordered from top to bottom (by getBoundingClientRect).
   * Called once at init to avoid repeated DOM reads.
   */
  private buildTextStack() {
    const withTop = this.textScene.texts.map((t: Text) => ({
      text: t,
      top: t.element.getBoundingClientRect().top,
    }))
    withTop.sort((a, b) => a.top - b.top)
    this.textStack = withTop.map((x) => x.text)
  }

  setEventListeners() {
    this.experience.on(EVENTS.Menu.itemClick, (e: unknown) => {
      const element =
        e != null &&
        typeof e === "object" &&
        "htmlElement" in e &&
        (e as { htmlElement: Element }).htmlElement
          ? (e as { htmlElement: Element }).htmlElement
          : (e as Element)
      const textInstance = this.textScene.texts.find(
        (t) => t.element === element,
      )
      if (!textInstance) {
        return
      }

      const sectionTarget = textInstance.element.getAttribute(
        "data-section-target",
      )
      if (!sectionTarget) {
        return
      }

      const sectionTargetElement = document.querySelector(
        `[data-name="${sectionTarget}"]`,
      ) as HTMLElement
      if (!sectionTargetElement) {
        return
      }

      this.experience.lenis?.start()

      this.experience.lenis?.scrollTo(sectionTargetElement.offsetTop, {
        duration: 1,
        lock: true,
        easing: (x) => Math.sin((x * Math.PI) / 2),
      })

      setTimeout(() => {
        this.experience.isMenuOpen = false
        this.close()
      }, 500)
    })
    this.experience.on(EVENTS.Menu.open, () => {
      this.open()
    })
    this.experience.on(EVENTS.Menu.close, () => {
      this.close()
    })
  }

  addMenuToColorNode(sceneColor: THREE.TSL.ShaderCallNodeInternal) {
    // Render targets are sampled flipped on Y in this WebGPU pipeline
    const menuUv = vec2(uv().x, uv().y.oneMinus())
    const menuOutput = texture(this.fbo.texture, menuUv)
    const perlinUv = uv().mul(this.settings.perlinUvScale)
    const rawPerlin = texture(this.perlin, perlinUv).r
    const poweredPerlin = rawPerlin.pow(this.settings.perlinPower)
    const perlinAlpha = clamp(
      clamp(poweredPerlin.mul(this.settings.perlinMultiplier), 0, 1).mul(
        this.settings.perlinAmplitude,
      ),
      0,
      1,
    )

    // Blur strength scales with perlinAlpha: 0 = no blur, 1 = full blur
    const blurStrength = perlinAlpha.mul(this.settings.blurStrength)
    const blurredSceneColor = gaussianBlur(
      sceneColor,
      vec2(blurStrength, blurStrength),
    )
    // perlinAlpha 0 → sharp scene, perlinAlpha > 0 → blurred (amount = perlinAlpha)
    const sceneWithBlur = mix(sceneColor, blurredSceneColor, perlinAlpha)

    // Darken by perlinAlpha: at full perlin, brightness = 1 - darkenPercentage/100
    const minBrightness = float(1).sub(this.settings.darkenPercentage.mul(0.01))
    const darkenFactor = mix(float(1), minBrightness, perlinAlpha)
    const sceneWithBlurAndDarken = sceneWithBlur.mul(
      vec4(darkenFactor, darkenFactor, darkenFactor, 1),
    )

    const finalColor = mix(sceneWithBlurAndDarken, menuOutput, menuOutput.a)

    return finalColor
  }

  open() {
    if (this.perlinOpenTween) {
      this.perlinOpenTween.kill()
    }
    this.perlinOpenTween = gsap.to(this.settings.perlinMultiplier, {
      value: 10,
      duration: 3,
      ease: "power2.out",
    })
    this.revealDelayedCalls.forEach((tween) => tween.kill())
    this.revealDelayedCalls.length = 0
    const stagger = 0.06
    this.textStack.forEach((t, i) => {
      const tween = gsap.delayedCall(i * stagger, () => t.reveal())
      this.revealDelayedCalls.push(tween)
    })
  }

  close() {
    if (this.perlinOpenTween) {
      this.perlinOpenTween.kill()
      this.perlinOpenTween = undefined
    }
    this.revealDelayedCalls.forEach((tween) => tween.kill())
    this.revealDelayedCalls.length = 0
    this.textStack.forEach((t) => t.leave())
    gsap.to(this.settings.perlinMultiplier, {
      value: 0,
      duration: 1,
      ease: "power2.inOut",
    })
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "🔍 Menu",
      expanded: true,
    })

    const settings = {
      blurStrength: this.settings.blurStrength.value,
      perlinPower: this.settings.perlinPower.value,
      perlinMultiplier: this.settings.perlinMultiplier.value,
      perlinAmplitude: this.settings.perlinAmplitude.value,
      perlinUvScale: this.settings.perlinUvScale.value,
      darkenPercentage: this.settings.darkenPercentage.value,
    }

    folder
      .addBinding(settings, "blurStrength", {
        min: 0,
        max: 20,
        step: 0.5,
        label: "Blur strength",
      })
      .on("change", (ev: { value: number }) => {
        this.settings.blurStrength.value = ev.value
      })

    folder
      .addBinding(settings, "perlinPower", {
        min: 0.1,
        max: 5,
        step: 0.1,
        label: "Perlin power",
      })
      .on("change", (ev: { value: number }) => {
        this.settings.perlinPower.value = ev.value
      })

    folder
      .addBinding(settings, "perlinMultiplier", {
        min: 0,
        max: 10,
        step: 0.01,
        label: "Perlin multiplier",
      })
      .on("change", (ev: { value: number }) => {
        this.settings.perlinMultiplier.value = ev.value
      })

    folder
      .addBinding(settings, "perlinAmplitude", {
        min: 0,
        max: 2,
        step: 0.01,
        label: "Perlin amplitude",
      })
      .on("change", (ev: { value: number }) => {
        this.settings.perlinAmplitude.value = ev.value
      })

    folder
      .addBinding(settings, "perlinUvScale", {
        min: 0,
        max: 1,
        step: 0.01,
        label: "Perlin UV scale",
      })
      .on("change", (ev: { value: number }) => {
        this.settings.perlinUvScale.value = ev.value
      })

    folder
      .addBinding(settings, "darkenPercentage", {
        min: 0,
        max: 100,
        step: 1,
        label: "Darken %",
      })
      .on("change", (ev: { value: number }) => {
        this.settings.darkenPercentage.value = ev.value
      })
  }

  update(params?: { delta: number }) {
    this.textScene.update(params ?? { delta: 0 }, this.fbo)
  }

  resize() {
    this.fbo.setSize(this.sizes.width, this.sizes.height)
    this.textScene.resize()
  }
}
